import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Mail, Check, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../services/api';

const translations = {
  uk: {
    title: 'VeonFox Premium',
    subtitle: 'Довічний статус',
    message: 'Від імені команди VeonFox раді вручити вам статус VeonFox Premium на довічній основі. Вам будуть першими доступні всі майбутні оновлення та нові функції нашої платформи. Крім того, при запуску мобільної версії VeonFox ви отримаєте пріоритетний ранній доступ до преміум бета-тестування.',
    emailLabel: 'Електронна пошта для отримання оновлень та бета-версій',
    button: 'Підтвердити отримання статусу',
    success: 'Статус успішно підтверджено! Ласкаво просимо до VeonFox Premium. 🦊👑',
    errorEmail: 'Будь ласка, вкажіть коректну адресу електронної пошти 📧'
  },
  ru: {
    title: 'VeonFox Premium',
    subtitle: 'Пожизненный статус',
    message: 'От лица команды VeonFox рады вручить вам статус VeonFox Premium на пожизненной основе. Вам будут первыми доступны все будущие обновления и новые функции нашей платформы. Кроме того, при запуске мобильной версии VeonFox вы получите приоритетный ранний доступ к премиюм бета-тестированию.',
    emailLabel: 'Электронная почта для получения обновлений и бета-версий',
    button: 'Подтвердить получение статуса',
    success: 'Статус успешно подтвержден! Добро пожаловать в VeonFox Premium. 🦊👑',
    errorEmail: 'Пожалуйста, укажите корректный адрес электронной почты 📧'
  },
  en: {
    title: 'VeonFox Premium',
    subtitle: 'Lifetime Status',
    message: 'On behalf of the VeonFox team, we are pleased to grant you lifetime VeonFox Premium status. You will be the first to receive all future updates and new platform features. Additionally, upon the launch of the VeonFox mobile version, you will get priority early access to the premium beta testing.',
    emailLabel: 'Email address for receiving updates and beta versions',
    button: 'Confirm Status Activation',
    success: 'Status successfully confirmed! Welcome to VeonFox Premium. 🦊👑',
    errorEmail: 'Please provide a valid email address 📧'
  }
};

export default function PremiumWelcomeModal() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine language (fall back to uk or en depending on active language)
  const currentLang = i18n.language || 'en';
  const langKey = currentLang.startsWith('uk') ? 'uk' : currentLang.startsWith('ru') ? 'ru' : 'en';
  const tLocal = translations[langKey];

  useEffect(() => {
    // Show only for the specific user ID who hasn't seen it yet
    if (user?.id === '447d9bcc-3679-4d3f-893c-2a7b9a6c41d4') {
      const hasSeen = localStorage.getItem('premium_welcome_seen_' + user.id);
      if (!hasSeen) {
        setEmail(user.email || '');
        setIsOpen(true);
      }
    }
  }, [user]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error(tLocal.errorEmail);
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Send email to backend database
      await api.post('/users/premium/confirm-email', { email: email.trim() });
      
      if (user?.id) {
        localStorage.setItem('premium_welcome_seen_' + user.id, 'true');
      }
      toast.success(tLocal.success);
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to confirm premium email:', err);
      toast.error(
        currentLang.startsWith('uk') 
          ? 'Помилка збереження. Спробуйте ще раз.' 
          : currentLang.startsWith('ru') 
            ? 'Ошибка сохранения. Попробуйте еще раз.' 
            : 'Error saving email. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Ambient Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Glowing Mesh Wrapper */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative w-full max-w-md rounded-3xl p-[1px] bg-gradient-to-b from-amber-400 via-yellow-500 to-indigo-600 shadow-[0_0_50px_rgba(245,158,11,0.25)] overflow-hidden"
          >
            {/* Inner Glassmorphic Container */}
            <div className="bg-black/90 dark:bg-zinc-950/95 backdrop-blur-xl rounded-[23px] p-8 text-center relative overflow-hidden">
              
              {/* Dynamic light rays / blobs */}
              <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.12)_0%,rgba(0,0,0,0)_60%)] pointer-events-none" />
              
              {/* Animated Floating Star Badge */}
              <div className="relative mx-auto w-20 h-20 bg-amber-500/10 border border-amber-500/35 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-amber-500/5 overflow-hidden">
                <motion.div
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 10, 
                    repeat: Infinity, 
                    ease: "linear" 
                  }}
                  className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(245,158,11,0.2)_0%,transparent_60%)]"
                />
                <Star className="w-10 h-10 text-amber-400 drop-shadow-[0_0_12px_rgba(245,158,11,0.6)]" strokeWidth={1.8} />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-black uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-300 mb-3 font-unbounded">
                {tLocal.title}
              </h2>
              <div className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black uppercase tracking-widest rounded-full inline-flex items-center gap-1.5 mb-6 font-unbounded">
                <Sparkles className="w-3 h-3 animate-pulse" /> {tLocal.subtitle}
              </div>

              {/* Message */}
              <p className="text-zinc-300 dark:text-zinc-200 text-xs leading-relaxed mb-6 font-medium text-left bg-white/[0.02] border border-white/[0.04] p-4.5 rounded-2xl">
                {tLocal.message}
              </p>

              {/* Email Form */}
              <form onSubmit={handleConfirm} className="space-y-4">
                <div className="space-y-1.5 text-left">
                  <label className="text-[9px] font-black uppercase tracking-wider text-zinc-500 ml-2 font-unbounded leading-normal">
                    {tLocal.emailLabel}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-black/50 border border-zinc-800 outline-none text-sm text-white focus:border-amber-500/80 focus:ring-1 focus:ring-amber-500/30 transition-all font-semibold"
                    />
                  </div>
                </div>

                {/* Confirm Button */}
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 disabled:opacity-50 text-black text-xs font-black uppercase tracking-widest font-unbounded rounded-2xl transition-all shadow-lg shadow-amber-500/10 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    '...'
                  ) : (
                    <>
                      <span>{tLocal.button}</span>
                      <Check className="w-4 h-4 stroke-[3]" />
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
